import { useEffect, useState } from 'react';
import type { PrintJob } from '../lib/print';
import { closePrint } from '../lib/print';

/**
 * The A4 preview. Nothing reaches the printer unseen.
 *
 * On screen: white paper on the app background, black text, and a dashed rule
 * wherever a new page starts. On paper: the bar and the note drop away
 * (`.no-print`), the sheet loses its border and margin, and `page-break-after`
 * does the real breaking.
 *
 * The document is plain HTML with `@media print` — which, per BRIEF.md, is what
 * makes A4 printing work from the tablet at all. TEST IT on the real Android
 * tablet and Bluetooth printer before building anything further on it:
 * `@page { size: A4 }` and `page-break-after` are honoured inconsistently by
 * Android's print service.
 */

interface Props {
  job: PrintJob;
}

export default function PrintPreview({ job }: Props) {
  const [printing, setPrinting] = useState(false);

  // The browser uses document.title as the filename when the tablet "prints to
  // PDF", so a saved day report is named rather than "localhost".
  useEffect(() => {
    const previous = document.title;
    document.title = job.label;
    return () => {
      document.title = previous;
    };
  }, [job.label]);

  // Escape backs out, same as the Back button.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') closePrint();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  function print() {
    setPrinting(true);
    // Let the button's own state paint before the print dialog blocks the page.
    requestAnimationFrame(() => {
      try {
        window.print();
      } finally {
        setPrinting(false);
      }
    });
  }

  return (
    <div className="preview">
      <div className="prevbar no-print">
        <button type="button" className="btn" onClick={closePrint}>
          Back
        </button>
        <button type="button" className="btn pri" onClick={print} disabled={printing}>
          Print / save as PDF
        </button>
        <span className="sub" style={{ marginLeft: 'auto' }}>
          {job.label}
        </span>
      </div>

      <div className="a4">{job.document}</div>

      <div className="pgnote no-print">
        This is how it comes out on A4. Dashed lines show where a new page starts.
      </div>
    </div>
  );
}

/**
 * A page break. Dashed on screen so the clerk can see where the paper ends;
 * `page-break-after: always` when it actually prints.
 */
export function PageBreak({ children }: { children: React.ReactNode }) {
  return <div className="pbreak">{children}</div>;
}

/** Lays out a list of pages with a break after every one but the last. */
export function Pages({ pages }: { pages: React.ReactNode[] }) {
  return (
    <>
      {pages.map((page, index) =>
        index < pages.length - 1 ? (
          <PageBreak key={index}>{page}</PageBreak>
        ) : (
          <div key={index}>{page}</div>
        ),
      )}
    </>
  );
}

/**
 * The stamp at the top of a document.
 *
 * Dashed border for UNAPPROVED, solid for APPROVED with the date and time it
 * was approved — so the two are told apart across a room, not by reading them.
 */
export function Stamp({
  approved, when, label,
}: {
  approved: boolean;
  when?: string | null;
  label?: string;
}) {
  if (approved) {
    return (
      <div className="stamp stamp-ok">
        APPROVED
        {when && ` — ${new Date(when).toLocaleString('en-GB')}`}
      </div>
    );
  }
  return <div className="stamp stamp-draft">UNAPPROVED — {label ?? 'for owner review'}</div>;
}

/** The ruled header every document carries. */
export function DocHeader({
  doc, title, sub,
}: {
  doc: string;
  title: string;
  sub?: string;
}) {
  return (
    <div className="dochead">
      <div className="docname">{doc}</div>
      <div className="doctitle">{title}</div>
      {sub && <div className="docsub">{sub}</div>}
    </div>
  );
}
